import { FC, Suspense } from 'react';
import { useLocation, Link as RouterLink } from 'react-router';
import { Box, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { PermissionedView } from '@cvt/components/Permissioned';
import { ListLoading } from '@cvt/components/layout/ListLoading';

import { useSpecialization } from '@modules/Specializations/hooks/useSpecialization';
import { UniversityRow, UniversityRowSkeleton } from '@modules/Universities/components/UniversityRow';
import { InfiniteUniversitiesList } from '@modules/Universities/partials/InfiniteUniversitiesList';
import { SpecializationDetails } from '@modules/Specializations/partials/SpecializationDetails';

import { ViewHeader } from '@shared/components/ViewHeader';

interface ViewProps {
  major: Majors.Major;
  specialization: Specializations.Specialization;
}

export const ViewMajorSpecializationView: FC<ViewProps> = ({ major, specialization }) => {
  const router = useRouter();
  const location = useLocation();

  return (
    <Stack direction="column">
      <Grid container spacing={2}>
        <Grid size={12}>
          <ViewHeader
            category={major.title}
            title={specialization.title}
            slots={{
              left: (
                <IconButton component={RouterLink} to={router.majors.view(major.id).specializations.path} color="primary">
                  <ArrowBack/>
                </IconButton>
              ),
            }}
          />
          <SpecializationDetails {...specialization} />
          {/* UNIVERSITES */}
          <Box
            p={2}
            width="100%"
            sx={theme => ({ backgroundColor: theme.palette.tertiary.light })}
          >
            <Typography variant="subtitle1" mb={0.5}>Universities offering this specialization</Typography>
            <ErrorBoundary>
              <PermissionedView permission="universities.list">
                <Paper variant="outlined">
                  <Suspense key={location.key} fallback={(
                    <ListLoading
                      disablePadding
                      repeat={3}
                      renderItem={() => <UniversityRowSkeleton/>}
                    />
                  )}>
                    <InfiniteUniversitiesList
                      filters={{
                        specialization: specialization.id,
                        limit: 20,
                      }}
                      renderSkeleton={() => <UniversityRowSkeleton/>}
                      renderItem={university => <UniversityRow key={university.id} {...university} linkTo={router.universities.view(university.id).majors.view(major.id).specializations.view(specialization.id).path} />}
                    />
                  </Suspense>
                </Paper>
              </PermissionedView>
            </ErrorBoundary>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};

interface Props {
  id: number;
  major: Majors.Major;
}

export const ViewMajorSpecialization: FC<Props> = ({ id, major }) => {

  const { specialization } = useSpecialization({
    id,
  });

  if (!specialization) {
    return null;
  }

  return (
    <ViewMajorSpecializationView major={major} specialization={specialization} />
  );
};
